/* eslint-disable no-var */
import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getErrorMessage } from 'utils';

import { authStorage } from 'modules/auth/authStorage';
import NotificationService from 'modules/notifications/notificationService';
import {
  clearBudgetData,
  clearGlobalFilterDimensions,
  clearSelectedColumns,
  getViewsStart,
  getWidgetDataFailure,
  invalidateApiData,
  resetAdvancedConfiguration,
} from 'modules/performance/performanceSlice';

import { localStorageService } from 'services/localStorage';
import { ADVERTISER_ID } from 'services/localStorage/constants';

import { orgApi } from './organizationApi';
import {
  Advertiser,
  AdvertisersPagingRequestValues,
  AdvertisersPagingResponse,
  Agency,
  Campaign,
  CreateAdvertiserRequest,
  CreateAgencyRequest,
  UpdateAdvertiserRequest,
  UpdateAgencyRequest,
} from './organizationApiTypes';
import {
  getAgenciesStart,
  getAgenciesFailure,
  getAgenciesSuccess,
  getAgenciesByRoleStart,
  getAgenciesByRoleFailure,
  getAgenciesByRoleSuccess,
  getCampaignsStart,
  updateAdvertiserStart,
  createAdvertiserStart,
  createAdvertiserSuccess,
  createAdvertiserFailure,
  updateAdvertiserSuccess,
  updateAdvertiserFailure,
  deleteAdvertiserStart,
  deleteAdvertiserSuccess,
  deleteAdvertiserFailure,
  getCampaignsFailure,
  getCampaignsSuccess,
  updateAgencySuccess,
  updateAgencyFailure,
  createAgencySuccess,
  createAgencyFailure,
  createAgencyStart,
  updateAgencyStart,
  getCurrentAgencyStart,
  getCurrentAgencySuccess,
  getCurrentAgencyFailure,
  getCurrentAdvertiserSuccess,
  getCurrentAdvertiserFailure,
  getCurrentAdvertiserStart,
  setCurrentAdvertiser,
  getAdvertisersSuccess,
  getAdvertisersFailure,
  getAdvertisersStart,
  setDsp,
  getDsp,
} from './organizationSlice';

function* getAgenciesFlow() {
  try {
    const dsp: string = yield select(getDsp);
    const response: Agency[] = yield call(orgApi.getAgencies, dsp);
    const authToken: string = yield call(authStorage.getIdToken);
    const agencies = updateImageUrls(response, authToken);

    const savedAdvertiserId = localStorageService.getItem<string>(`${ADVERTISER_ID}_${dsp}`) || '';
    const isAdvertiserInAgencies = agencies.some((agency) =>
      agency.advertisers.some((advertiser) => advertiser.id === savedAdvertiserId),
    );

    if (savedAdvertiserId && isAdvertiserInAgencies) {
      yield put(setCurrentAdvertiser(savedAdvertiserId));
    } else {
      const defaultAdvertiserId = isAdvertiserInAgencies
        ? savedAdvertiserId
        : agencies.find((a) => a.advertisers.length)?.advertisers[0]?.id || '';

      yield put(setCurrentAdvertiser(defaultAdvertiserId));
    }
    yield put(getAgenciesSuccess(agencies));
  } catch (error) {
    yield put(getAgenciesFailure(getErrorMessage(error)));
  }
}

function* getAgenciesByRoleFlow() {
  try {
    const dsp: string = yield select(getDsp);
    const response: Agency[] = yield call(orgApi.getAgenciesByRole, dsp);

    yield put(getAgenciesByRoleSuccess(response));
  } catch (error) {
    yield put(getAgenciesByRoleFailure(getErrorMessage(error)));
  }
}

function* getCurrentAgencyFlow(action: PayloadAction<string>) {
  try {
    const response: Agency = yield call(orgApi.getAgency, action.payload);
    const authToken: string = yield call(authStorage.getIdToken);

    const agency = updateImageUrls([response], authToken);

    yield put(getCurrentAgencySuccess(agency[0]));
  } catch (error) {
    yield put(getCurrentAgencyFailure(getErrorMessage(error)));
  }
}

function* getCurrentAdvertiserFlow(action: PayloadAction<string>) {
  try {
    const response: Advertiser = yield call(orgApi.getAdvertiser, action.payload);

    yield put(getCurrentAdvertiserSuccess(response));
  } catch (error) {
    yield put(getCurrentAdvertiserFailure(getErrorMessage(error)));
  }
}

function* createAdvertiserFlow(action: PayloadAction<CreateAdvertiserRequest>) {
  try {
    yield call(orgApi.createAdvertiser, action.payload);
    const authToken: string = yield call(authStorage.getIdToken);
    const dsp: string = yield select(getDsp);
    const response: Agency[] = yield call(orgApi.getAgencies, dsp);
    const agencies = updateImageUrls(response, authToken);

    yield put(createAdvertiserSuccess(agencies));
  } catch (error) {
    yield put(createAdvertiserFailure(getErrorMessage(error)));
  }
}

function* updateAdvertiserFlow(action: PayloadAction<UpdateAdvertiserRequest>) {
  try {
    const { agencyId, ...update } = action.payload;

    yield call(orgApi.updateAdvertiser, update);
    // After the update, so a rename made to avoid a name clash in the target agency is saved first.
    if (agencyId) {
      yield call(orgApi.moveAdvertiser, update.advertiserId, agencyId);
      NotificationService.success('Advertiser moved');
    }
    const authToken: string = yield call(authStorage.getIdToken);
    const dsp: string = yield select(getDsp);
    const response: Agency[] = yield call(orgApi.getAgencies, dsp);
    const agencies = updateImageUrls(response, authToken);

    yield put(updateAdvertiserSuccess(agencies));
  } catch (error) {
    yield put(updateAdvertiserFailure(getErrorMessage(error)));
  }
}

function* deleteAdvertiserFlow(action: PayloadAction<string>) {
  try {
    yield call(orgApi.deleteAdvertiser, action.payload);
    // Refetching agencies drops the advertiser from the header picker, re-selects a default if it
    // was the current one, and re-runs the settings grid query.
    yield call(getAgenciesFlow);

    yield put(deleteAdvertiserSuccess());
  } catch (error) {
    yield put(deleteAdvertiserFailure(getErrorMessage(error)));
  }
}

function* getCampaignsFlow(action: PayloadAction<string>) {
  try {
    const response: Campaign[] = yield call(orgApi.getCampaigns, action.payload);

    yield put(getCampaignsSuccess(response));
  } catch (error) {
    yield put(getCampaignsFailure(getErrorMessage(error)));
  }
}

function* updateAgencyFlow(action: PayloadAction<UpdateAgencyRequest>) {
  try {
    const dsp: string = yield select(getDsp);

    yield call(orgApi.updateAgency, action.payload);
    const authToken: string = yield call(authStorage.getIdToken);
    const response: Agency[] = yield call(orgApi.getAgencies, dsp);
    const agencies = updateImageUrls(response, authToken);

    yield put(updateAgencySuccess(agencies));
  } catch (error) {
    yield put(updateAgencyFailure(getErrorMessage(error)));
  }
}

function* createAgencyFlow(action: PayloadAction<CreateAgencyRequest>) {
  try {
    const agency: Agency = yield call(orgApi.createAgency, action.payload);

    yield put(createAgencySuccess(agency));
  } catch (error) {
    yield put(createAgencyFailure(getErrorMessage(error)));
  }
}

function updateImageUrls(agencies: Agency[], authToken: string): Agency[] {
  return agencies.map((agency) => ({
    ...agency,
    logoUrl: agency.logoUrl ? `${agency.logoUrl}&idToken=${authToken}` : agency.logoUrl,
    wordMarkUrl: agency.wordMarkUrl
      ? `${agency.wordMarkUrl}&idToken=${authToken}`
      : agency.wordMarkUrl,
  }));
}

function* changeAdvertiserFlow(action: PayloadAction<string>) {
  try {
    const dsp: string = yield select(getDsp);

    localStorageService.setItem(`${ADVERTISER_ID}_${dsp}`, action.payload);

    yield put(clearBudgetData());
    yield put(clearSelectedColumns());
    yield put(resetAdvancedConfiguration());
    yield put(clearGlobalFilterDimensions());
    yield put(invalidateApiData());

    yield put(getViewsStart(action.payload));
  } catch (error) {
    yield put(getWidgetDataFailure(getErrorMessage(error)));
  }
}

function* getAdvertisersFlow(action: PayloadAction<AdvertisersPagingRequestValues>) {
  try {
    const response: AdvertisersPagingResponse = yield call(orgApi.getAdvertisers, action.payload);

    yield put(getAdvertisersSuccess(response));
  } catch (error) {
    yield put(getAdvertisersFailure(getErrorMessage(error)));
  }
}

function* changeDspFlow() {
  try {
    yield call(getAgenciesFlow);
  } catch (error) {
    yield put(getWidgetDataFailure(getErrorMessage(error)));
  }
}

export function* orgSagas() {
  yield all([
    takeLatest(getAgenciesStart.type, getAgenciesFlow),
    takeLatest(getAgenciesByRoleStart.type, getAgenciesByRoleFlow),
    takeLatest(getCurrentAgencyStart.type, getCurrentAgencyFlow),
    takeLatest(getCurrentAdvertiserStart.type, getCurrentAdvertiserFlow),
    takeLatest(createAdvertiserStart.type, createAdvertiserFlow),
    takeLatest(updateAdvertiserStart.type, updateAdvertiserFlow),
    takeLatest(deleteAdvertiserStart.type, deleteAdvertiserFlow),
    takeLatest(getCampaignsStart.type, getCampaignsFlow),
    takeLatest(createAgencyStart.type, createAgencyFlow),
    takeLatest(updateAgencyStart.type, updateAgencyFlow),
    takeLatest(setCurrentAdvertiser.type, changeAdvertiserFlow),
    takeLatest(getAdvertisersStart.type, getAdvertisersFlow),
    takeLatest(setDsp.type, changeDspFlow),
  ]);
}
